import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Backpack, Crown, Globe2, Plane, Plus, Search, Shuffle, SlidersHorizontal, Sofa, Users, Wallet, X, ChevronDown, MapPin, Check } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { useModal } from '../context/ModalContext';
import { SECTION_THEME } from '../utils/sectionTheme';
import BannerScene from '../components/common/BannerScene';
import { normalizeTravelBudget, TRAVEL_BUDGET_OPTIONS, TRAVEL_GENDER_OPTIONS } from '../utils/travelPostLabels';
import TravelPostCard from '../components/travel/TravelPostCard';
import TravelFilterPanel from '../components/travel/TravelFilterPanel';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useMobileHeader } from '../context/MobileHeaderContext';
import FullScreenFilterModal from '../components/common/FullScreenFilterModal';
const desktopGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px'
};

const GENDER_ICONS = { '': Users, any: Shuffle, female: Users, male: Users };
const BUDGET_ICONS = { backpacker: Backpack, budget: Wallet, standard: Sofa, premium: Crown };

const pillStyle = (isSelected) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '9px 14px',
  color: isSelected ? '#ffffff' : '#334155',
  background: isSelected ? SECTION_THEME.travel.accent : '#ffffff',
  border: isSelected ? `1px solid ${SECTION_THEME.travel.accent}` : '1px solid #dbe4ee',
  borderRadius: '999px',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: '750',
  cursor: 'pointer',
  whiteSpace: 'nowrap'
});


export default function TravelMates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Multi-country filter via ?countries=a,b,c (legacy single ?country= still readable)
  const countriesParam = searchParams.get('countries') || searchParams.get('country') || '';
  const countryFilter = useMemo(
    () => countriesParam.split(',').filter(Boolean),
    [countriesParam]
  );
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const isDesktop = useIsDesktop();
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [budgetFilters, setBudgetFilters] = useState([]);
  const [dateStartFilter, setDateStartFilter] = useState('');
  const [dateEndFilter, setDateEndFilter] = useState('');
  const isFirstLoad = useRef(true);
  const { showAlert } = useModal();
  const { setHeaderLeftAction } = useMobileHeader();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadPosts = useCallback(async () => {
    if (isFirstLoad.current) setIsLoading(true);
    try {
      const query = supabase
        .from('travel_posts')
        .select(`
          id, destination, country, countries, trip_start_date, trip_end_date,
          budget_range, max_companions, gender_pref, description, status, created_at, image_urls,
          organizer_id,
          profiles:organizer_id ( id, name, avatar ),
          travel_applications ( status, applicant:profiles!applicant_id ( id, name, avatar ) )
        `)
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('loadPosts error:', err);
      showAlert('錯誤', '載入揪旅伴貼文失敗，請稍後再試');
    } finally {
      setIsLoading(false);
      isFirstLoad.current = false;
    }
  }, [showAlert]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const availableCountries = useMemo(() => {
    const values = new Set();
    posts.forEach(post => {
      const countries = post.countries?.length > 0 ? post.countries : (post.country ? [post.country] : []);
      countries.forEach(country => values.add(country));
    });
    return Array.from(values).sort((first, second) => first.localeCompare(second, 'zh-TW'));
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    return posts.filter(post => {
      const countries = post.countries?.length > 0 ? post.countries : (post.country ? [post.country] : []);
      const matchesCountry = countryFilter.length === 0 || countries.some(country => countryFilter.includes(country));
      const matchesGender = !genderFilter || (post.gender_pref || 'any') === genderFilter;
      const matchesBudget = budgetFilters.length === 0 || budgetFilters.includes(normalizeTravelBudget(post.budget_range));
      const postStartDate = post.trip_start_date || post.trip_end_date;
      const postEndDate = post.trip_end_date || post.trip_start_date;
      const hasDateFilter = dateStartFilter || dateEndFilter;
      const matchesDate = !hasDateFilter || (
        Boolean(postStartDate) &&
        (!dateStartFilter || postEndDate >= dateStartFilter) &&
        (!dateEndFilter || postStartDate <= dateEndFilter)
      );
      const searchableText = [post.destination, post.description, post.profiles?.name, ...countries]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return matchesCountry && matchesGender && matchesBudget && matchesDate && (!keyword || searchableText.includes(keyword));
    });
  }, [budgetFilters, countryFilter, dateEndFilter, dateStartFilter, genderFilter, posts, searchQuery]);

  const activeFilterCount = [
    countryFilter.length > 0,
    genderFilter !== '',
    budgetFilters.length > 0,
    dateStartFilter !== '' || dateEndFilter !== ''
  ].filter(Boolean).length;

  const updateCountryFilter = useCallback((country) => {
    const nextCountries = countryFilter.includes(country)
      ? countryFilter.filter(item => item !== country)
      : [...countryFilter, country];
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('country');
    if (nextCountries.length > 0) nextParams.set('countries', nextCountries.join(','));
    else nextParams.delete('countries');
    setSearchParams(nextParams, { replace: true });
  }, [countryFilter, searchParams, setSearchParams]);

  const clearCountryFilter = useCallback(() => {
    setSearchParams(previous => {
      const nextParams = new URLSearchParams(previous);
      nextParams.delete('country');
      nextParams.delete('countries');
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setGenderFilter('');
    setBudgetFilters([]);
    setDateStartFilter('');
    setDateEndFilter('');
    setSearchParams(previous => {
      const nextParams = new URLSearchParams(previous);
      nextParams.delete('country');
      nextParams.delete('countries');
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleBudgetFilter = useCallback((budget) => {
    setBudgetFilters(previous => previous.includes(budget)
      ? previous.filter(item => item !== budget)
      : [...previous, budget]);
  }, []);

  const mobileFilterContent = useMemo(() => (
    <TravelFilterPanel
      availableCountries={availableCountries}
      countryFilter={countryFilter}
      updateCountryFilter={updateCountryFilter}
      genderFilter={genderFilter}
      setGenderFilter={setGenderFilter}
      budgetFilters={budgetFilters}
      toggleBudgetFilter={toggleBudgetFilter}
      dateStartFilter={dateStartFilter}
      setDateStartFilter={setDateStartFilter}
      dateEndFilter={dateEndFilter}
      setDateEndFilter={setDateEndFilter}
      activeFilterCount={activeFilterCount}
      searchQuery={searchQuery}
      onClear={clearFilters}
      variant="drawer"
    />
  ), [activeFilterCount, availableCountries, budgetFilters, clearFilters, countryFilter, dateEndFilter, dateStartFilter, genderFilter, searchQuery, toggleBudgetFilter, updateCountryFilter]);

  useEffect(() => {
    if (isDesktop) {
      setHeaderLeftAction(null);
      return;
    }

    const currentCountryText = countryFilter.length > 0
      ? (countryFilter.length === 1 ? countryFilter[0] : `已選 ${countryFilter.length} 國`)
      : '全世界';

    setHeaderLeftAction(
      <button
        onClick={() => setIsCountryModalOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          padding: '8px 12px 8px 4px',
          fontFamily: 'inherit',
          fontSize: '16px',
          fontWeight: '800',
          color: '#1e293b',
          cursor: 'pointer'
        }}
      >
        <MapPin size={18} strokeWidth={2.5} color="#1e293b" />
        <span style={{ marginLeft: '6px' }}>{currentCountryText}</span>
        <ChevronDown size={18} color="#64748b" />
      </button>
    );

    return () => setHeaderLeftAction(null);
  }, [isDesktop, countryFilter, setHeaderLeftAction]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #eef4f9', borderTop: '3px solid #4e8bca', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const renderDesktopFilterBar = () => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <button type="button" onClick={clearCountryFilter} style={pillStyle(countryFilter.length === 0)}>全部</button>
        {availableCountries.map(country => {
          const isSelected = countryFilter.includes(country);
          return (
            <button key={country} type="button" aria-pressed={isSelected} onClick={() => updateCountryFilter(country)} style={pillStyle(isSelected)}>
              <Globe2 size={14} aria-hidden="true" />{country}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        {[{ value: '', label: '全部性別' }, ...TRAVEL_GENDER_OPTIONS].map(option => {
          const isSelected = genderFilter === option.value;
          const Icon = GENDER_ICONS[option.value];
          return (
            <button key={option.value || 'all'} type="button" onClick={() => setGenderFilter(option.value)} style={pillStyle(isSelected)}>
              <Icon size={14} />{option.label}
            </button>
          );
        })}

        <div style={{ width: '1px', height: '22px', background: '#e2e8f0', margin: '0 2px' }} />

        {TRAVEL_BUDGET_OPTIONS.map(option => {
          const isSelected = budgetFilters.includes(option.value);
          const Icon = BUDGET_ICONS[option.value];
          return (
            <button key={option.value} type="button" aria-pressed={isSelected} onClick={() => toggleBudgetFilter(option.value)} style={pillStyle(isSelected)}>
              <Icon size={14} />{option.label}
            </button>
          );
        })}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type="date" aria-label="篩選開始日期" value={dateStartFilter} max={dateEndFilter || undefined} onChange={event => setDateStartFilter(event.target.value)} style={{ boxSizing: 'border-box', minHeight: '38px', padding: '8px 10px', color: '#334155', background: '#ffffff', border: '1px solid #dbe4ee', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px' }} />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
          <input type="date" aria-label="篩選結束日期" value={dateEndFilter} min={dateStartFilter || undefined} onChange={event => setDateEndFilter(event.target.value)} style={{ boxSizing: 'border-box', minHeight: '38px', padding: '8px 10px', color: '#334155', background: '#ffffff', border: '1px solid #dbe4ee', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px' }} />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #eef4f9 0%, #f0faf9 15%, #f8fffe 40%, #ffffff 100%)',
      padding: '0 0 80px 0',
      width: '100%',
      overflowX: 'clip'
    }}>
      {/* Gradient Hero Header */}
      <div style={{
        background: SECTION_THEME.travel.bannerBase,
        padding: isMobile ? '18px 20px 20px' : (isDesktop ? '20px 32px 22px' : '24px 32px 26px'),
        minHeight: isDesktop ? '176px' : '140px',
        borderRadius: '0 0 32px 32px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <BannerScene src={SECTION_THEME.travel.bannerImage} alt="旅伴熊熊吉祥物" isMobile={isMobile} />
        <div style={{ maxWidth: isDesktop ? '1360px' : '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: isDesktop ? '58%' : '100%' }}>
            <div style={{ minWidth: 0, maxWidth: isDesktop ? '100%' : '75%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <h1 style={{
                  fontSize: isDesktop ? '1.875rem' : '1.5rem',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.01em',
                  lineHeight: '1.25'
                }}>
                  <span style={{ color: '#1e2f42' }}>找到</span><span style={{ color: SECTION_THEME.travel.accent }}>一起出發</span><span style={{ color: '#1e2f42' }}>的人</span>
                </h1>
              </div>
              <p style={{
                color: '#4c6d8c',
                fontSize: isDesktop ? '0.9375rem' : '0.875rem',
                margin: 0,
                fontWeight: '500',
                lineHeight: '1.5'
              }}>
                因為有人同行，每趟旅程都更精彩
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <Search size={17} color="#94a3b8" style={{ position: 'absolute', top: '50%', left: '14px', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  type="search"
                  aria-label="搜尋旅伴行程"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="搜尋目的地、國家、關鍵字..."
                  style={{
                    boxSizing: 'border-box', width: '100%', minHeight: '46px',
                    padding: searchQuery ? '11px 42px 11px 42px' : '11px 14px 11px 42px',
                    color: '#0f172a', background: 'rgba(255,255,255,0.96)',
                    border: '1px solid rgba(255,255,255,0.72)', borderRadius: '15px',
                    outline: 'none', boxShadow: '0 8px 22px rgba(30,64,99,0.16)',
                    fontFamily: 'inherit', fontSize: '14px'
                  }}
                />
                {searchQuery && (
                  <button type="button" aria-label="清除搜尋" onClick={() => setSearchQuery('')} style={{ position: 'absolute', top: '50%', right: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', padding: 0, color: '#64748b', background: '#eef2f6', border: 'none', borderRadius: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
              {isDesktop ? (
                <button type="button" aria-label="搜尋旅伴行程" style={{ flexShrink: 0, minWidth: '84px', height: '46px', padding: '0 22px', color: '#ffffff', background: SECTION_THEME.travel.accent, border: 'none', borderRadius: '15px', fontSize: '14px', fontWeight: '800', cursor: 'pointer' }}>搜尋</button>
              ) : (
                <button
                  type="button"
                  aria-label="篩選旅伴行程"
                  onClick={() => setIsFilterModalOpen(true)}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, width: '46px', minWidth: '46px', height: '46px', padding: 0,
                    color: activeFilterCount > 0 ? '#376ba1' : '#64748b',
                    background: activeFilterCount > 0 ? '#eaf2f8' : 'rgba(255,255,255,0.96)',
                    border: '1px solid rgba(255,255,255,0.72)', borderRadius: '15px',
                    boxShadow: '0 8px 22px rgba(30,64,99,0.16)', cursor: 'pointer'
                  }}
                >
                  <SlidersHorizontal size={19} aria-hidden="true" />
                  {activeFilterCount > 0 && <span style={{ position: 'absolute', top: '-5px', right: '-5px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '19px', height: '19px', padding: '0 4px', color: '#ffffff', background: '#376ba1', border: '2px solid #ffffff', borderRadius: '999px', fontSize: '9px', fontWeight: '800' }}>{activeFilterCount}</span>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: isDesktop ? '1360px' : '1200px', margin: '0 auto', padding: '20px 16px 0' }}>
        {isDesktop && renderDesktopFilterBar()}

        {/* 貼文列表 */}
        {filteredPosts.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '320px', backgroundColor: 'white', borderRadius: '24px',
            padding: '48px 24px', textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)'
          }}>
            <Plane style={{ width: '64px', height: '64px', color: '#d1d5db', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
              {posts.length === 0 ? '目前沒有揪旅伴貼文' : '找不到符合條件的旅程'}
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>{posts.length === 0 ? '成為第一個發起的人吧！' : '試試其他條件，或清除篩選。'}</p>
            <button
              onClick={posts.length === 0 ? () => navigate('/travel/create') : clearFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '14px 28px', borderRadius: '16px', border: 'none',
                background: '#4e8bca', color: 'white',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(78, 139, 202, 0.3)'
              }}
            >
              {posts.length === 0 ? <Plus style={{ width: '18px', height: '18px' }} /> : <X style={{ width: '18px', height: '18px' }} />}
              {posts.length === 0 ? '發起揪旅伴' : '清除篩選'}
            </button>
          </div>
        ) : (
          <div style={isDesktop ? desktopGridStyle : {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '16px'
          }}>
            {filteredPosts.map(post => (
              <TravelPostCard
                key={post.id}
                post={post}
                onOpen={() => navigate(`/travel/${post.id}`)}
                onOrganizerOpen={() => navigate(`/travel/user/${post.organizer_id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 建立貼文 FAB */}
      <button
        onClick={() => navigate('/travel/create')}
        style={{
          position: 'fixed', bottom: '84px', right: '20px',
          width: '52px', height: '52px', borderRadius: '50%',
          background: '#4e8bca', color: 'white', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(78, 139, 202, 0.4)', cursor: 'pointer',
          zIndex: 100,
        }}
      >
        <Plus size={24} />
      </button>

      {/* Mobile Filter Modal */}
      <FullScreenFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="篩選旅伴行程"
        onClear={activeFilterCount > 0 ? clearFilters : undefined}
        onApply={() => setIsFilterModalOpen(false)}
        applyLabel={`顯示 ${filteredPosts.length} 筆結果`}
      >
        {mobileFilterContent}
      </FullScreenFilterModal>

      {/* Country Selection Modal */}
      <FullScreenFilterModal
        isOpen={isCountryModalOpen}
        onClose={() => setIsCountryModalOpen(false)}
        title="選擇國家"
        onClear={countryFilter.length > 0 ? clearCountryFilter : undefined}
        onApply={() => setIsCountryModalOpen(false)}
        applyLabel="確定"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button
            onClick={() => { clearCountryFilter(); setIsCountryModalOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px', background: 'transparent', border: 'none',
              borderBottom: '1px solid #f1f5f9', cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: countryFilter.length === 0 ? '700' : '500', color: countryFilter.length === 0 ? '#1e293b' : '#64748b' }}>全世界 (不限國家)</span>
            {countryFilter.length === 0 && <Check size={20} color="#4e8bca" />}
          </button>
          {availableCountries.map(country => {
            const isSelected = countryFilter.includes(country);
            return (
              <button
                key={country}
                onClick={() => updateCountryFilter(country)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px', background: 'transparent', border: 'none',
                  borderBottom: '1px solid #f1f5f9', cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Globe2 size={18} color={isSelected ? '#4e8bca' : '#94a3b8'} />
                  <span style={{ fontSize: '16px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#1e293b' : '#64748b' }}>{country}</span>
                </div>
                {isSelected && <Check size={20} color="#4e8bca" />}
              </button>
            );
          })}
        </div>
      </FullScreenFilterModal>

    </div>
  );
}
